import { KeyboardDatePicker } from "@material-ui/pickers";
import React, { useCallback, useMemo, useState } from "react";
import CcFieldLayout, {
  resolveFieldMessage,
  useCcInputStyles,
} from "./CcFieldLayout";

function parseStringToDate(dateValue) {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return dateValue;
  if (typeof dateValue === "string") {
    const date = new Date(dateValue);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function formatDateToString(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const CcDatePicker = React.memo(
  ({
    label,
    required = false,
    value,
    onChange,
    format = "yyyy-MM-dd",
    clearable = true,
    error,
    helperText,
    hint,
    id,
    KeyboardButtonProps,
    InputLabelProps: _inputLabelProps,
    // Clicking anywhere in the field opens the calendar, not only the icon
    // (Kim Pascual, Esmere, 2026-09-10). Typing still works while it is open:
    // the popover does not take focus away from the field.
    openOnClick = true,
    InputProps,
    PopoverProps,
    ...props
  }) => {
    const inputClasses = useCcInputStyles();
    const [open, setOpen] = useState(false);
    const { hasError, message } = resolveFieldMessage({
      error,
      helperText,
      hint,
    });
    const parsedValue = useMemo(() => parseStringToDate(value), [value]);
    const handleChange = useCallback(
      (newValue) => {
        onChange?.(formatDateToString(newValue));
      },
      [onChange],
    );

    const picker = (
      <KeyboardDatePicker
        autoOk
        variant="inline"
        inputVariant="outlined"
        format={format}
        clearable={clearable}
        value={parsedValue}
        onChange={handleChange}
        id={id}
        error={hasError}
        helperText={undefined}
        label={undefined}
        className={inputClasses.root}
        InputLabelProps={{ shrink: false, style: { display: "none" } }}
        FormHelperTextProps={{ style: { display: "none" } }}
        placeholder={format.toLowerCase()}
        KeyboardButtonProps={{
          "aria-label": "change date",
          ...KeyboardButtonProps,
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        InputProps={{
          ...InputProps,
          onClick: (event) => {
            InputProps?.onClick?.(event);
            if (openOnClick && !props.disabled && !props.readOnly)
              setOpen(true);
          },
        }}
        PopoverProps={{
          disableAutoFocus: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
          ...PopoverProps,
        }}
        {...props}
      />
    );

    if (!label && !message) {
      return picker;
    }

    return (
      <CcFieldLayout
        label={label}
        required={required}
        error={hasError ? message : undefined}
        hint={!hasError ? message : undefined}
        htmlFor={id}
      >
        {picker}
      </CcFieldLayout>
    );
  },
);

CcDatePicker.displayName = "CcDatePicker";

export default CcDatePicker;
